import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BusinessHours } from "@/components/BusinessHours";
import { canonicalLink, canonicalUrl, geoMeta, SITE_URL } from "@/lib/seo";
import { businessHeadScripts } from "@/lib/businessJsonLd";

const TITLE = "Accessibility Statement | Texas Made Shade & Covers";
const DESCRIPTION =
  "Our commitment to accessibility: we are working toward WCAG 2.2 Level AA. Report an accessibility problem by phone or email and we will respond and help you complete any request.";

export const Route = createFileRoute("/accessibility")({
  head: () => ({
    links: [canonicalLink("/accessibility")],
    meta: [
      ...geoMeta(),
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Accessibility Statement — Texas Made Shade & Covers" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: canonicalUrl("/accessibility") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      ...businessHeadScripts(),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Accessibility Statement", item: canonicalUrl("/accessibility") },
          ],
        }),
      },
    ],
  }),
  component: AccessibilityPage,
});

const LAST_UPDATED = "September 2026";

function AccessibilityPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="bg-cream">
        <section className="border-b-8 border-navy-deep bg-navy-deep py-14 text-white lg:py-20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <p className="font-display text-xs uppercase tracking-[0.3em] text-red">
              Accessibility Statement
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-tight md:text-5xl">
              Accessibility
            </h1>
            <p className="mt-4 max-w-3xl text-base text-white/85 md:text-lg">
              Texas Made Shade &amp; Covers is committed to making this website usable by everyone,
              including people with disabilities. If you cannot use part of this site, we want to
              hear about it — and we will help you finish what you came to do another way.
            </p>
            <p className="mt-3 text-xs uppercase tracking-widest text-white/70">
              Last updated: {LAST_UPDATED}
            </p>
          </div>
        </section>

        <section className="py-12 lg:py-16">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-3 lg:px-8">
            <div className="space-y-8 lg:col-span-2">
              <article className="border-2 border-navy-deep bg-white p-6 shadow-card">
                <h2 className="font-display text-2xl font-bold uppercase text-navy-deep">
                  Our commitment
                </h2>
                <p className="mt-3 text-base leading-relaxed text-navy-deep/80">
                  We are working toward conforming to the Web Content Accessibility Guidelines
                  (WCAG) 2.2 at Level AA, the standard referenced by the U.S. Department of Justice
                  for web accessibility under the Americans with Disabilities Act (ADA). We build
                  and test this site ourselves, and accessibility is an ongoing effort, not a
                  finished project.
                </p>
              </article>

              <article className="border-2 border-navy-deep bg-white p-6 shadow-card">
                <h2 className="font-display text-2xl font-bold uppercase text-navy-deep">
                  How to report an accessibility problem
                </h2>
                <p className="mt-3 text-base leading-relaxed text-navy-deep/80">
                  If you encounter a barrier on this website — something you cannot read, focus, or
                  operate — contact us and describe the page and the problem. Please include a phone
                  number or email so we can follow up.
                </p>
                <ul className="mt-4 space-y-3 text-base text-navy-deep/80">
                  <li className="flex items-center gap-3">
                    <Phone className="h-5 w-5 shrink-0 text-red" aria-hidden="true" focusable="false" />
                    <a href="tel:+18303909588" className="font-semibold underline decoration-red decoration-2 underline-offset-4 hover:text-red">
                      +1 (830) 390-9588
                    </a>
                    <span className="text-navy-deep/70">(Mon–Thu 8:30–5:00, Fri 8:30–4:00 CT)</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Mail className="h-5 w-5 shrink-0 text-red" aria-hidden="true" focusable="false" />
                    <a href="mailto:office@texasmadeshade.com" className="break-all font-semibold underline decoration-red decoration-2 underline-offset-4 hover:text-red">
                      office@texasmadeshade.com
                    </a>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-1 h-5 w-5 shrink-0 text-red" aria-hidden="true" focusable="false" />
                    <span>959 East Kingsbury Street, Seguin, TX 78155</span>
                  </li>
                </ul>
                <p className="mt-4 text-base leading-relaxed text-navy-deep/80">
                  We aim to acknowledge accessibility reports within two business days and to fix
                  confirmed barriers as quickly as we reasonably can.
                </p>
              </article>

              <article className="border-2 border-navy-deep bg-white p-6 shadow-card">
                <h2 className="font-display text-2xl font-bold uppercase text-navy-deep">
                  If you cannot use part of this website
                </h2>
                <p className="mt-3 text-base leading-relaxed text-navy-deep/80">
                  You do not need the website to do business with us. If any part of this site
                  prevents you from getting a quote, asking a question, or placing an order, contact
                  us by phone or email and a member of our team will complete the request with you
                  directly — including taking your measurements, photos and project details over the
                  phone, at the shop, or at your property in our service area.
                </p>
              </article>

              <article className="border-2 border-navy-deep bg-white p-6 shadow-card">
                <h2 className="font-display text-2xl font-bold uppercase text-navy-deep">
                  What we build for
                </h2>
                <ul className="mt-3 space-y-2 text-base leading-relaxed text-navy-deep/80">
                  <li>• Keyboard operation of every interactive element, with visible focus indicators.</li>
                  <li>• Text alternatives (alt text) for meaningful images.</li>
                  <li>• Color contrast that meets or approaches WCAG AA on text and controls.</li>
                  <li>• Form fields with programmatic labels, so screen readers announce them correctly.</li>
                  <li>• Respect for your system's reduced-motion preference on animations.</li>
                  <li>• Responsive layouts that work with device text sizing and screen magnification.</li>
                </ul>
                <p className="mt-4 text-base leading-relaxed text-navy-deep/80">
                  This statement was last reviewed in {LAST_UPDATED}. As we add pages and features,
                  we continue testing against WCAG 2.2 AA.
                </p>
              </article>
            </div>

            <div className="space-y-6">
              <div className="border-2 border-navy-deep bg-white p-5 shadow-card">
                <h2 className="font-display text-lg font-bold uppercase text-navy-deep">
                  Prefer to just call?
                </h2>
                <p className="mt-2 text-sm text-navy-deep/75">
                  Talk to the shop directly — no forms, no menus, no hold queue.
                </p>
                <a
                  href="tel:+18303909588"
                  className="mt-4 inline-flex items-center gap-2 bg-red px-5 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-red-deep"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" focusable="false" /> +1 (830) 390-9588
                </a>
              </div>

              <div className="border-2 border-navy-deep bg-white p-5 shadow-card">
                <h2 className="font-display text-lg font-bold uppercase text-navy-deep">
                  Visit the shop
                </h2>
                <p className="mt-2 flex items-start gap-2 text-sm text-navy-deep/80">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-red" aria-hidden="true" focusable="false" />
                  959 East Kingsbury Street
                  <br />
                  Seguin, TX 78155
                </p>
                <div className="mt-4">
                  <BusinessHours />
                </div>
              </div>

              <div className="border-2 border-navy-deep bg-white p-5 shadow-card">
                <h2 className="font-display text-lg font-bold uppercase text-navy-deep">
                  Related policies
                </h2>
                <ul className="mt-3 space-y-2 text-sm">
                  <li>
                    <Link to="/privacy-policy" className="font-medium text-navy-deep underline decoration-red decoration-2 underline-offset-4 hover:text-red">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" className="font-medium text-navy-deep underline decoration-red decoration-2 underline-offset-4 hover:text-red">
                      Contact &amp; About Us
                    </Link>
                  </li>
                  <li>
                    <Link to="/local-faqs" className="font-medium text-navy-deep underline decoration-red decoration-2 underline-offset-4 hover:text-red">
                      Service Area FAQs
                    </Link>
                  </li>
                </ul>
                <Link
                  to="/estimate-quotes"
                  className="mt-5 inline-flex items-center gap-2 bg-red px-5 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-red-deep"
                >
                  Request a Quote <ArrowRight className="h-4 w-4" aria-hidden="true" focusable="false" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
